/**
 * Market Conventions & Reference Data Service
 * Handles fetching market conventions and manual reference rate entry
 */

import { apiClient, APIResponse } from './api';

export interface DateConvention {
  id: string;
  descriptive_key: string;
  tradeType: string;
  currencyPair: string;
  defaultTradeDate: string;
  defaultValueDate: string;
  defaultExpiryDate: string;
  defaultPaymentDate: string;
  useBusinessDays: boolean;
}

export interface FixingExpiryConvention {
  id: string;
  descriptive_key: string;
  tradeType: string;
  currencyPair: string;
  defaultFixingReference: string;
  defaultFixingOffset: string;
  defaultSettlementType: string;
  defaultSettlementCurrency: string;
}

/**
 * Get all market date conventions
 */
export const getDateConventions = async (): Promise<DateConvention[]> => {
  const response = await apiClient.get<DateConvention[]>('/api/v1/reference-data/market-conventions/date-conventions');
  return response.data;
};

/**
 * Get all market fixing/expiry conventions
 */
export const getFixingExpiryConventions = async (): Promise<FixingExpiryConvention[]> => {
  const response = await apiClient.get<FixingExpiryConvention[]>('/api/v1/reference-data/market-conventions/fixing-expiry-conventions');
  return response.data;
};

export interface BusinessCenterHolidays {
  centers: string[];
  weekendDays: number[];   // Python convention: Mon=0 … Sun=6
  holidays: Record<string, string[]>;  // year → sorted ISO dates
}

/**
 * Get merged holiday calendar for one or more business centers
 */
export const getBusinessCenterHolidays = async (centers: string[]): Promise<BusinessCenterHolidays> => {
  const param = [...new Set(centers)].join(',');
  const response = await apiClient.get<BusinessCenterHolidays>(
    `/api/v1/reference-data/business-centers/holidays?centers=${param}`
  );
  return response.data;
};


// ── Manual reference rate entry ─────────────────────────────────────

export interface ReferenceRateData {
  collection: string;
  date: string;
  rate: number;
  source: string;
  alreadyExisted?: boolean;
  enteredByEmail?: string;
  enteredAt?: string;
}

/**
 * Save a manual reference rate (e.g. Bloomberg 11 AM EUR/USD BFIX).
 * Idempotent: if a rate already exists for the collection/date, returns
 * the existing rate without overwriting.
 */
export const saveReferenceRate = async (
  collection: string,
  date: string,
  rate: number
): Promise<APIResponse<ReferenceRateData>> => {
  return apiClient.post<ReferenceRateData>(
    '/api/v1/reference-data/rates',
    { collection, date, rate }
  );
};

/**
 * Get an existing reference rate for a collection and date.
 * Returns data: null if no rate exists (not an error).
 */
export const getReferenceRate = async (
  collection: string,
  date: string
): Promise<APIResponse<ReferenceRateData | null>> => {
  return apiClient.get<ReferenceRateData | null>(
    `/api/v1/reference-data/rates?collection=${encodeURIComponent(collection)}&date=${encodeURIComponent(date)}`
  );
};

/**
 * Trigger the fixing service for a specific client and date.
 * The fixing endpoint is at /api/v1/fixing/run (not under /reference-data).
 */
export const runFixing = async (
  clientId: string,
  targetDate: string
): Promise<APIResponse<Record<string, any>>> => {
  return apiClient.post<Record<string, any>>(
    '/api/v1/fixing/run',
    { clientId, targetDate }
  );
};
